import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { formatLocation, formatPrice, eventStart, initials } from "../../../feedCard";
import { ReactComponent as LocationIcon } from "../../../../../../media/icons/linear-location.svg";
import styles from "./eventStripCard.module.css";

/**
 * One card of the upcoming-events panel (GROWTH-7131; image card since GROWTH-7573): a 16:9 image
 * with the month/day badge and the price pill over it, then the weekday and time, the title, the
 * location, and a footer with the community and a Details link. With no image_url, or one that fails
 * to load, the media area is a block with the event's initials — never a broken-image glyph.
 *
 * Renders a GET /api-v2/feed/upcoming-events item, whose `details.starts_at` is already the event's
 * computed NEXT occurrence, not necessarily its first one (FeedCardPresenter's shape is otherwise
 * unchanged, so formatLocation/formatPrice are the exact same helpers a full FeedCard uses).
 *
 * @param {Object} props
 * @param {Object} props.item one FeedItem, type "event"
 * @param {Object} [props.linkState] the router state its links carry to the listing page (GROWTH-7576)
 */
const EventStripCard = ({ item, linkState }) => {
  const { t } = useTranslation();
  const [imageFailed, setImageFailed] = useState(false);
  const details = item.details || {};
  const startsAt = eventStart(details);
  const location = formatLocation(item.location);
  const priceText = formatPrice(details.price, t);
  const href = `/public-listing/${item.id}`;

  return (
    <article className={styles.card}>
      <div className={styles.media}>
        <span className={styles.mediaFallback} aria-hidden="true">
          {initials(item.name)}
        </span>
        {item.image_url && !imageFailed ? (
          <img className={styles.image} src={item.image_url} alt="" loading="lazy" onError={() => setImageFailed(true)} />
        ) : null}

        {startsAt ? (
          <span className={styles.dateBadge}>
            <span className={styles.dateBadgeMonth}>{format(startsAt, "MMM")}</span>
            <span className={styles.dateBadgeDay}>{format(startsAt, "d")}</span>
          </span>
        ) : null}
        {priceText ? <span className={styles.pricePill}>{priceText}</span> : null}
      </div>

      <div className={styles.body}>
        {startsAt ? <span className={styles.when}>{format(startsAt, "EEE · h:mm a")}</span> : null}

        <Link to={href} state={linkState} className={styles.title}>
          {item.name}
        </Link>

        {location ? (
          <span className={styles.location}>
            <LocationIcon aria-hidden="true" /> {location}
          </span>
        ) : null}

        <span className={styles.footer}>
          <span className={styles.communityName}>{item.community_name || ""}</span>
          <Link to={href} state={linkState} className={styles.details}>
            {t("dashboard_feed_cta_details")} <span aria-hidden="true">→</span>
          </Link>
        </span>
      </div>
    </article>
  );
};

export default EventStripCard;
